import { create } from '@/shared/lib/zustand';
import { immer } from 'zustand/middleware/immer';
import { SingleFacilityTabId } from './types';

type FacilityTabsState = {
  activeTabId: SingleFacilityTabId;
};

type FacilityTabsActions = {
  setActiveTab: (tabId: SingleFacilityTabId) => void;
};

/**
 * The active Facilities tab, held globally rather than in component state.
 *
 * Portfolio Mode and Single Mode render two different component trees, so
 * local state was lost on every mode switch and always fell back to General.
 * Keeping it here means the tab survives the switch.
 *
 * The header also reads it, to disable the date controls on tabs that ignore a
 * date range (Floor Plan, Info).
 */
export const useFacilityTabsStore = create<
  FacilityTabsState & FacilityTabsActions
>()(
  immer((set) => ({
    activeTabId: SingleFacilityTabId.General,
    setActiveTab: (tabId) => {
      set((state) => {
        state.activeTabId = tabId;
      });
    },
  }))
);

/** Tabs whose content is not date-scoped, so the date filter is disabled. */
export const DATELESS_TABS: SingleFacilityTabId[] = [
  SingleFacilityTabId.FloorPlan,
  SingleFacilityTabId.Info,
];

/**
 * Tabs that only make sense over whole, finished months, so the header date
 * controls switch to month pickers and the 1M/6M/1Y chips are re-based onto
 * completed months (ST #137).
 *
 * Financial metrics are billed monthly: a part-month range would compare a few
 * days of revenue against a full month of expenses.
 */
export const MONTHS_ONLY_TABS: SingleFacilityTabId[] = [
  SingleFacilityTabId.Financial,
];
